import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { FONT_SYSTEM } from '../tokens';

// The one fixed, inset-0 overlay the gen-3 set builds on (Drawer,
// BottomSheet, ActionSheet, the Forms preview). It portals to <body>, dims
// the page, and hosts a single dialog panel placed by `placement`:
//   center — a centred panel (dialogs)
//   right  — pinned to the right edge, full height (drawers)
//   bottom — anchored to the bottom edge (phone sheets)
//   full   — the panel fills the viewport (full-screen phone views)
//
// Behaviour every overlay needs, in one place:
//   • Esc and a click on the dimmed backdrop call onClose. With overlays
//     stacked (a sheet over a drawer) only the topmost one reacts.
//   • Focus moves into the panel on open, Tab cycles inside it, and focus
//     returns to whatever opened it once it closes. If that element is gone
//     by then (a deleted card, a re-rendered row), focus goes to
//     `returnFocusRef` instead, when given.
//   • The panel portals out of PageShell, so it sets the v3 type itself: the
//     system font stack and line-height 1.5, with form controls inheriting
//     the font (as PageShell does).
//   • The page behind stops scrolling while any overlay is open.
// The panel is the element with role="dialog"; give it a name with `label`
// or `labelledBy`.

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

// Open overlays, oldest first. Only the last one handles keys.
const stack = [];

let lockCount = 0;
let previousOverflow = '';
const lockScroll = () => {
  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
};
const unlockScroll = () => {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) document.body.style.overflow = previousOverflow;
};

const focusablesIn = (el) =>
  Array.from(el.querySelectorAll(FOCUSABLE)).filter(
    (node) => !node.hasAttribute('disabled') && node.tabIndex !== -1
  );

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(40px); opacity: 0; }
  to { transform: none; opacity: 1; }
`;

const slideLeft = keyframes`
  from { transform: translateX(32px); opacity: 0; }
  to { transform: none; opacity: 1; }
`;

const popIn = keyframes`
  from { transform: translateY(12px) scale(0.98); opacity: 0; }
  to { transform: none; opacity: 1; }
`;

const PANEL_ANIMATION = {
  center: popIn,
  right: slideLeft,
  bottom: slideUp,
  full: fadeIn,
};

const PLACEMENT = {
  center: { alignItems: 'center', justifyContent: 'center', padding: 16 },
  right: { alignItems: 'stretch', justifyContent: 'flex-end', padding: 0 },
  bottom: { alignItems: 'flex-end', justifyContent: 'center', padding: 0 },
  full: { alignItems: 'stretch', justifyContent: 'stretch', padding: 0 },
};

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  box-sizing: border-box;
  animation: ${fadeIn} 160ms ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Panel = styled.div`
  position: relative;
  box-sizing: border-box;
  outline: none;
  font-family: ${FONT_SYSTEM};
  line-height: 1.5;
  animation: ${(p) => PANEL_ANIMATION[p.$placement] || popIn} 220ms
    cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  button,
  input,
  select,
  textarea {
    font-family: inherit;
  }
`;

const canFocus = (el) =>
  !!el && typeof el.focus === 'function' && document.body.contains(el);

const ModalOverlay = ({
  open,
  onClose,
  children,
  label,
  labelledBy,
  placement,
  zIndex,
  backdrop,
  closeOnBackdrop,
  panelStyle,
  initialFocusRef,
  returnFocusRef,
}) => {
  const panelRef = useRef(null);
  const pressStartedOnBackdrop = useRef(false);
  // Latest props for the effect below, which runs once per opening.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const focusTargetRef = useRef(initialFocusRef);
  focusTargetRef.current = initialFocusRef;
  const returnTargetRef = useRef(returnFocusRef);
  returnTargetRef.current = returnFocusRef;

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    stack.push(token);
    lockScroll();
    const opener = document.activeElement;

    const panel = panelRef.current;
    if (panel) {
      const target = focusTargetRef.current;
      const preferred = target && target.current;
      const first = preferred || focusablesIn(panel)[0] || panel;
      first.focus();
    }

    const onKeyDown = (e) => {
      if (stack[stack.length - 1] !== token) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        if (onCloseRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = focusablesIn(panelRef.current);
      if (!nodes.length) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }
      const firstNode = nodes[0];
      const lastNode = nodes[nodes.length - 1];
      const active = document.activeElement;
      const inside = panelRef.current.contains(active);
      if (e.shiftKey && (active === firstNode || !inside)) {
        e.preventDefault();
        lastNode.focus();
      } else if (!e.shiftKey && (active === lastNode || !inside)) {
        e.preventDefault();
        firstNode.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = stack.indexOf(token);
      if (i !== -1) stack.splice(i, 1);
      unlockScroll();
      const fallback =
        returnTargetRef.current && returnTargetRef.current.current;
      if (canFocus(opener) && opener !== document.body) opener.focus();
      else if (canFocus(fallback)) fallback.focus();
    };
  }, [open]);

  if (!open) return null;

  const onMouseDown = (e) => {
    pressStartedOnBackdrop.current = e.target === e.currentTarget;
  };
  // A drag that starts inside the panel and ends on the backdrop (selecting
  // text, say) must not close it, so the press has to start there too.
  const onClick = (e) => {
    if (
      closeOnBackdrop &&
      e.target === e.currentTarget &&
      pressStartedOnBackdrop.current &&
      onCloseRef.current
    ) {
      onCloseRef.current();
    }
    pressStartedOnBackdrop.current = false;
  };

  return ReactDOM.createPortal(
    <Backdrop
      role="presentation"
      data-overlay="true"
      onMouseDown={onMouseDown}
      onClick={onClick}
      style={{ zIndex, background: backdrop, ...PLACEMENT[placement] }}
    >
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        $placement={placement}
        style={panelStyle}
      >
        {children}
      </Panel>
    </Backdrop>,
    document.body
  );
};

ModalOverlay.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  children: PropTypes.node,
  label: PropTypes.string,
  labelledBy: PropTypes.string,
  placement: PropTypes.oneOf(['center', 'right', 'bottom', 'full']),
  zIndex: PropTypes.number,
  backdrop: PropTypes.string,
  closeOnBackdrop: PropTypes.bool,
  panelStyle: PropTypes.object,
  initialFocusRef: PropTypes.shape({ current: PropTypes.any }),
  // Where focus goes on close when the element that opened it is gone.
  returnFocusRef: PropTypes.shape({ current: PropTypes.any }),
};

ModalOverlay.defaultProps = {
  onClose: undefined,
  children: null,
  label: undefined,
  labelledBy: undefined,
  placement: 'center',
  zIndex: 1000,
  backdrop: 'rgba(0, 0, 0, 0.6)',
  closeOnBackdrop: true,
  panelStyle: undefined,
  initialFocusRef: undefined,
  returnFocusRef: undefined,
};

export default ModalOverlay;
